import type { BillingInterval, PaymentMethod, Plan } from "../../../shared/types.js";

export interface BillingUpdatedMessage {
  type: "ZENPOST_BILLING_UPDATED";
  subscription_plan: Plan;
  subscription_period: BillingInterval;
  payment_method?: PaymentMethod;
  /** Auto-login URL for first purchases — the parent should navigate the top window here. */
  login_url?: string;
  /**
   * Set when the update is a cancellation (renewal stopped, or money returned). The app refreshes
   * the subscription but keeps the sheet open so the buyer reads the final screen; an app that
   * does not know the field closes it as before.
   */
  action?: "cancellation";
}

/** The buyer pressed Continue on a final screen that asked the app to stay open. */
export interface BillingCloseMessage {
  type: "ZENPOST_BILLING_CLOSE";
}

/** True when running inside app.zenpost.ai (iframe) or a payment popup. */
export function isEmbeddedBillingContext(): boolean {
  try {
    return (
      (window.parent && window.parent !== window) ||
      (window.opener && window.opener !== window)
    );
  } catch {
    return false;
  }
}

/** Notify app.zenpost.ai (iframe parent or popup opener). */
export function notifyParentBillingUpdated(msg: Omit<BillingUpdatedMessage, "type">): void {
  const payload: BillingUpdatedMessage = {
    type: "ZENPOST_BILLING_UPDATED",
    ...msg,
    // The app knows card (stripe) and pix (openpix); Stone is a card.
    ...(msg.payment_method === "stone" ? { payment_method: "stripe" as const } : {}),
  };
  try {
    if (window.opener && window.opener !== window) {
      window.opener.postMessage(payload, window.location.origin);
    }
  } catch {
    /* ignore */
  }
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, "*");
    }
  } catch {
    /* ignore */
  }
}

/** Ask app.zenpost.ai to close the sheet this page is embedded in. Older apps ignore it. */
export function notifyParentBillingClose(): void {
  const payload: BillingCloseMessage = { type: "ZENPOST_BILLING_CLOSE" };
  try {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(payload, "*");
    }
  } catch {
    /* ignore */
  }
}

/**
 * Send a first-purchase buyer into app.zenpost.ai using the auto-login URL.
 * Embedded (previews iframe/popup): hand the URL to the parent so it can
 * navigate the TOP window (a self-navigation would load the app inside the small
 * iframe). Standalone: navigate this window directly.
 */
export function goToAutoLogin(args: {
  loginUrl: string;
  plan: Plan;
  interval: BillingInterval;
  paymentMethod?: PaymentMethod;
}): void {
  if (isEmbeddedBillingContext()) {
    notifyParentBillingUpdated({
      subscription_plan: args.plan,
      subscription_period: args.interval,
      payment_method: args.paymentMethod,
      login_url: args.loginUrl,
    });
    return;
  }
  window.location.href = args.loginUrl;
}

/**
 * After integrated checkout payment succeeds: tell the Zenpost app and close popup if used.
 * Falls back to redirecting the top window when the parent is not reachable.
 */
export function completeIntegratedPaymentReturn(args: {
  plan: Plan;
  interval: BillingInterval;
  paymentMethod?: PaymentMethod;
  appReturnUrl?: string | null;
  /** When set, return to /integrated instead of the Zenpost app subscription page. */
  integratedReturnUrl?: string | null;
  /** Delay before close/redirect; use 0 when the caller already showed the identified screen. */
  actionDelayMs?: number;
}): "popup" | "parent" | "redirect" | "none" {
  notifyParentBillingUpdated({
    subscription_plan: args.plan,
    subscription_period: args.interval,
    payment_method: args.paymentMethod,
  });

  const delay = args.actionDelayMs ?? 0;

  if (window.opener && window.opener !== window) {
    window.setTimeout(() => window.close(), delay);
    return "popup";
  }

  if (args.integratedReturnUrl) {
    window.setTimeout(() => {
      window.location.href = args.integratedReturnUrl!;
    }, delay);
    return "redirect";
  }

  if (window.parent && window.parent !== window) {
    return "parent";
  }

  if (args.appReturnUrl) {
    window.setTimeout(() => {
      window.location.href = args.appReturnUrl!;
    }, delay);
    return "redirect";
  }

  return "none";
}
